"use client";

import { X } from "lucide-react";
import { type FC, type HTMLAttributes, type ReactNode, useEffect, useRef, useState } from "react";
import { IconButton } from "@/components/ui/icon-button";
import { useStableId } from "@/lib/use-stable-id";
import { cn } from "@/lib/utils";
import { DialogContext, type DialogContextValue, useDialog } from "./dialog.context";

const DIALOG_TRANSITION_MS = 200;

const WIDTHS = {
  md: "max-w-lg",
  lg: "max-w-2xl",
} as const;

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Chamado depois que a animação de saída termina e o `<dialog>` fecha. */
  onClosed?: () => void;
  width?: keyof typeof WIDTHS;
  backdropLabel?: string;
  className?: string;
  children: ReactNode;
}

const Root: FC<DialogProps> = ({
  open,
  onClose,
  onClosed,
  width = "md",
  backdropLabel = "Fechar",
  className,
  children,
}) => {
  const ref = useRef<HTMLDialogElement>(null);
  const [visible, setVisible] = useState(false);
  const base = useStableId();
  const titleId = `${base}-title`;
  const descriptionId = `${base}-description`;

  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      // Definido antes do `showModal()` para o leitor de tela já anunciar a descrição.
      if (d.querySelector(`[id="${descriptionId}"]`)) {
        d.setAttribute("aria-describedby", descriptionId);
      } else {
        d.removeAttribute("aria-describedby");
      }
      d.showModal();
      // Foco inicial no título, não no X, para não exibir o anel ao abrir com o mouse.
      const heading = document.getElementById(titleId);
      if (heading && d.contains(heading)) {
        heading.tabIndex = -1;
        heading.setAttribute("data-initial-focus", "");
        heading.focus({ preventScroll: true });
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else if (!open && d.open) {
      setVisible(false);
      const t = window.setTimeout(() => {
        d.close();
        onClosedRef.current?.();
      }, DIALOG_TRANSITION_MS);
      return () => window.clearTimeout(t);
    }
  }, [open, titleId, descriptionId]);

  // Intercepta o Esc para que a animação de saída rode.
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      onCloseRef.current();
    };
    d.addEventListener("cancel", onCancel);
    return () => d.removeEventListener("cancel", onCancel);
  }, []);

  const contextValue: DialogContextValue = {
    titleId,
    descriptionId,
    close: () => onCloseRef.current(),
  };

  return (
    <DialogContext.Provider value={contextValue}>
      <dialog
        ref={ref}
        aria-labelledby={titleId}
        data-visible={visible}
        className={cn(
          "m-0 h-full max-h-none w-full max-w-none border-0 bg-transparent p-0",
          "backdrop:bg-bg-dark/0 backdrop:backdrop-blur-none backdrop:transition-all backdrop:duration-200 backdrop:ease-out",
          "data-[visible=true]:backdrop:bg-bg-dark/60 data-[visible=true]:backdrop:backdrop-blur-sm",
        )}
      >
        <div className="pointer-events-none relative z-10 flex min-h-full items-center justify-center p-4">
          <div
            className={cn(
              "pointer-events-auto flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-lg bg-surface shadow-xl",
              "transform transition-all ease-out will-change-transform",
              WIDTHS[width],
              visible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
              className,
            )}
            style={{ transitionDuration: `${DIALOG_TRANSITION_MS}ms` }}
          >
            {children}
          </div>
        </div>

        {/* Depois do painel para não receber o foco inicial do `showModal()`. */}
        <button
          type="button"
          aria-label={backdropLabel}
          tabIndex={-1}
          onClick={() => onCloseRef.current()}
          className="fixed inset-0 z-0 cursor-default bg-transparent"
        />
      </dialog>
    </DialogContext.Provider>
  );
};

interface PartProps {
  className?: string;
  children?: ReactNode;
}

type BlockProps = HTMLAttributes<HTMLDivElement>;

const Header: FC<BlockProps> = ({ className, ...props }) => (
  <div className={cn("flex items-start gap-4 p-6", className)} {...props} />
);

const Title: FC<PartProps> = ({ className, children }) => {
  const { titleId } = useDialog();
  return (
    <h2 id={titleId} className={cn("font-display text-h6 font-bold text-fg-strong", className)}>
      {children}
    </h2>
  );
};

const Description: FC<PartProps> = ({ className, children }) => {
  const { descriptionId } = useDialog();
  return (
    <p id={descriptionId} className={cn("mt-1 text-body-sm text-fg-muted", className)}>
      {children}
    </p>
  );
};

interface CloseProps {
  label?: string;
  icon?: ReactNode;
  className?: string;
}

const Close: FC<CloseProps> = ({
  label = "Fechar",
  icon = <X aria-hidden className="h-5 w-5" />,
  className,
}) => {
  const { close } = useDialog();
  return (
    <IconButton
      variant="subtle"
      onClick={close}
      aria-label={label}
      className={cn("-mt-2 -mr-2 shrink-0 text-fg-muted", className)}
    >
      {icon}
    </IconButton>
  );
};

const Body: FC<BlockProps> = ({ className, ...props }) => (
  <div className={cn("overflow-y-auto px-6 pb-6", className)} {...props} />
);

const Footer: FC<BlockProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex flex-wrap justify-end gap-3 border-t border-border-subtle bg-bg-secondary/40 px-6 py-4",
      className,
    )}
    {...props}
  />
);

Root.displayName = "Dialog";
Header.displayName = "Dialog.Header";
Title.displayName = "Dialog.Title";
Description.displayName = "Dialog.Description";
Close.displayName = "Dialog.Close";
Body.displayName = "Dialog.Body";
Footer.displayName = "Dialog.Footer";

export const Dialog = Object.assign(Root, { Header, Title, Description, Close, Body, Footer });
